// Combobox — portal-into-dialog, allowCustom, keyboard nav, ARIA-correct.
// Built as a primitive so feature-specific wrappers (ModelPresetCombobox)
// supply their own option rendering via render props without re-implementing
// the keyboard / portal / dismiss machinery.
//
// CRITICAL portal rule (CLAUDE.md): when this lives inside a Radix Dialog
// the popup MUST mount inside the dialog content, NOT document.body.
// Body-portaled options are treated as "outside" by Radix DismissableLayer
// and clicking one closes the dialog before our onClick can fire — selection
// silently fails. Falls back to body when no dialog ancestor exists so the
// component still works on regular pages.
//
// Tab semantics: Tab does NOT auto-promote free-typed text to a custom
// value. Free text commits ONLY via Enter when allowCustom=true. Tab
// simply commits the highlighted option (if any) and moves focus to the
// next form field — matches platform combobox UX (auto-promoting on Tab
// surprised real users in usability passes; an explicit Enter is required).

import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { ChevronDown } from "lucide-react";
import { cn } from "@/utils";

export interface ComboboxOption<T = unknown> {
  /** Stable identifier — used for React keys and selection commit. */
  value: string;
  /** Filtered against the user's typed input (case-insensitive substring). */
  searchText: string;
  /** Caller payload returned through onSelect. */
  data: T;
  disabled?: boolean;
  disabledReason?: string;
}

export interface ComboboxRenderOptionArgs<T> {
  option: ComboboxOption<T>;
  active: boolean;
  selected: boolean;
}

export interface ComboboxProps<T> {
  /** Controlled input value (the visible text). */
  value: string;
  onChange: (next: string) => void;
  /** Filtered set of options to display. Caller pre-filters when async. */
  options: ComboboxOption<T>[];
  /** Fires when user picks an option (click / Enter on highlight / Tab). */
  onSelect: (option: ComboboxOption<T>) => void;
  /** Fires when user presses Enter on no-match input. Only when allowCustom=true. */
  onCustomCommit?: (raw: string) => void;
  allowCustom?: boolean;
  placeholder?: string;
  disabled?: boolean;
  /** Render slot for each option — caller controls layout. */
  renderOption: (args: ComboboxRenderOptionArgs<T>) => React.ReactNode;
  /** Slot rendered above the option list when present (loading/error/empty). */
  headerSlot?: React.ReactNode;
  /** Slot rendered when filtered options are empty AND no headerSlot. */
  emptySlot?: React.ReactNode;
  className?: string;
  inputClassName?: string;
  /** Optional additional ARIA label for the input. */
  ariaLabel?: string;
}

export interface ComboboxHandle {
  focus: () => void;
  close: () => void;
}

function ComboboxInner<T>(
  props: ComboboxProps<T>,
  ref: React.ForwardedRef<ComboboxHandle>,
) {
  const {
    value,
    onChange,
    options,
    onSelect,
    onCustomCommit,
    allowCustom = false,
    placeholder,
    disabled = false,
    renderOption,
    headerSlot,
    emptySlot,
    className,
    inputClassName,
    ariaLabel,
  } = props;

  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState<number>(-1);
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);
  const [anchorRect, setAnchorRect] = useState<{
    position: "fixed" | "absolute";
    top: number;
    left: number;
    width: number;
  } | null>(null);
  const listboxId = useId();

  useImperativeHandle(
    ref,
    () => ({
      focus: () => inputRef.current?.focus(),
      close: () => setOpen(false),
    }),
    [],
  );

  // Reset highlight when filter narrows past current index — prevents Enter
  // from selecting an option that has scrolled out of the visible set.
  useEffect(() => {
    if (activeIdx >= options.length || (activeIdx >= 0 && options[activeIdx]?.disabled)) {
      setActiveIdx(options.findIndex((option) => !option.disabled));
    }
  }, [options, activeIdx]);

  // Resolve portal target lazily on open.
  useLayoutEffect(() => {
    if (!open) {
      setPortalTarget(null);
      return;
    }
    const dialog = containerRef.current?.closest<HTMLElement>('[role="dialog"]');
    setPortalTarget(dialog ?? document.body);
  }, [open]);

  // Anchor under the input. Body portals use viewport-fixed coordinates.
  // Dialog portals use dialog-relative absolute coordinates because transformed
  // sheets create a containing block for fixed descendants.
  useLayoutEffect(() => {
    if (!open) {
      setAnchorRect(null);
      return;
    }
    const update = () => {
      const el = containerRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const target = portalTarget;
      if (target && target !== document.body) {
        const targetRect = target.getBoundingClientRect();
        setAnchorRect({
          position: "absolute",
          top: r.bottom - targetRect.top + 4,
          left: r.left - targetRect.left,
          width: r.width,
        });
        return;
      }
      setAnchorRect({ position: "fixed", top: r.bottom + 4, left: r.left, width: r.width });
    };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open, portalTarget]);

  // Outside-click closes. Both the input wrapper AND the portaled popup are
  // "inside" — clicking either is non-dismissive. Without this guard, an
  // option click outside containerRef would close the popup before onClick.
  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (containerRef.current?.contains(target)) return;
      if (popupRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [open]);

  const commitHighlighted = useCallback(() => {
    if (activeIdx >= 0 && activeIdx < options.length && !options[activeIdx]?.disabled) {
      onSelect(options[activeIdx]);
      setOpen(false);
      return true;
    }
    return false;
  }, [activeIdx, options, onSelect]);

  const nextEnabledIndex = useCallback((start: number, direction: 1 | -1) => {
    for (let i = start; i >= 0 && i < options.length; i += direction) {
      if (!options[i]?.disabled) return i;
    }
    return -1;
  }, [options]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;
    if (!open && (e.key === "ArrowDown" || e.key === "Enter")) {
      setOpen(true);
      e.preventDefault();
      return;
    }
    if (!open) return;
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setActiveIdx((i) => nextEnabledIndex(Math.max(i + 1, 0), 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        setActiveIdx((i) => nextEnabledIndex(i < 0 ? options.length - 1 : i - 1, -1));
        break;
      case "Enter": {
        e.preventDefault();
        if (commitHighlighted()) return;
        if (allowCustom && value.trim() !== "" && onCustomCommit) {
          onCustomCommit(value);
          setOpen(false);
        }
        break;
      }
      case "Escape":
        e.preventDefault();
        setOpen(false);
        break;
      case "Tab":
        // Commit highlighted option only — never auto-promote free text.
        // Allow default tab behavior (focus moves to next field) regardless
        // so the user is not trapped.
        commitHighlighted();
        break;
    }
  };

  const optionId = useCallback((i: number) => `${listboxId}-opt-${i}`, [listboxId]);
  const activeDescendantId =
    open && activeIdx >= 0 && activeIdx < options.length ? optionId(activeIdx) : undefined;

  const popup = useMemo(() => {
    if (!open || !anchorRect || !portalTarget) return null;
    const node = (
      <div
        ref={popupRef}
        role="listbox"
        id={listboxId}
        aria-label={ariaLabel}
        style={{
          position: anchorRect.position,
          top: anchorRect.top,
          left: anchorRect.left,
          width: anchorRect.width,
        }}
        className="z-[var(--z-popover)] max-h-72 overflow-y-auto rounded-lg border border-[var(--border)] bg-[var(--popover)] shadow-[var(--shadow-lg)]"
      >
        {headerSlot}
        {!headerSlot && options.length === 0 && emptySlot}
        {options.length > 0 &&
          options.map((opt, i) => {
            const active = i === activeIdx;
            return (
              <div
                key={opt.value}
                id={optionId(i)}
                role="option"
                aria-selected={active}
                aria-disabled={opt.disabled || undefined}
                title={opt.disabledReason}
                onMouseEnter={() => { if (!opt.disabled) setActiveIdx(i); }}
                onMouseDown={(e) => {
                  // mousedown so blur from input does not close the popup
                  // before onClick fires.
                  e.preventDefault();
                  if (opt.disabled) return;
                  onSelect(opt);
                  setOpen(false);
                }}
                className={cn(
                  "cursor-pointer px-3 py-2 text-sm",
                  active && "bg-[var(--accent)] text-[var(--accent-foreground)]",
                  opt.disabled && "cursor-not-allowed opacity-60",
                )}
              >
                {renderOption({ option: opt, active, selected: false })}
                {opt.disabledReason && (
                  <span className="mt-1 block text-xs text-[var(--muted-foreground)]">
                    {opt.disabledReason}
                  </span>
                )}
              </div>
            );
          })}
      </div>
    );
    return createPortal(node, portalTarget);
  }, [
    open,
    anchorRect,
    portalTarget,
    options,
    activeIdx,
    headerSlot,
    emptySlot,
    onSelect,
    renderOption,
    listboxId,
    ariaLabel,
    optionId,
  ]);

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={activeDescendantId}
          aria-label={ariaLabel}
          value={value}
          disabled={disabled}
          placeholder={placeholder}
          onChange={(e) => {
            onChange(e.target.value);
            if (!open) setOpen(true);
            setActiveIdx(-1);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          className={cn(
            "h-11 w-full rounded-lg border border-[var(--border)] bg-[var(--card)] px-4 pr-9",
            "text-sm text-[var(--foreground)]",
            "focus:border-[var(--ring)] focus:outline-none focus:shadow-[var(--shadow-focus-ring)]",
            "disabled:opacity-60 disabled:cursor-not-allowed",
            inputClassName,
          )}
        />
        <ChevronDown
          aria-hidden
          className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-[var(--muted-foreground)]"
        />
      </div>
      {popup}
    </div>
  );
}

const Combobox = forwardRef(ComboboxInner) as <T>(
  p: ComboboxProps<T> & { ref?: React.ForwardedRef<ComboboxHandle> },
) => React.ReactElement;

export default Combobox;
